import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Button, Switch, Tooltip, Modal, Form, Input, notification } from "antd";
import {
  PlusOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
  getProducts,
  changeProductOrder,
  updateProduct as updateProductAPI,
  updateCategory,
  removeCategory,
} from "../../../service";
import { colors, spacing, sizes, transitions } from "../../../styles/theme";
import { useNavigate } from "react-router-dom";
import { AddProductModal } from "../add-product-modal/AddProductModal";
import { useBranchContext } from "../../../contexts/branch-context";
import { useTourContext } from "../../../contexts/tour-context/useTourContext";
import { useAdditionsTour } from "../../../contexts/additions-tour-context/useAdditionsTour";

const CategoryContainer = styled.div`
  background: ${colors.gray100};
  border-radius: ${sizes.borderRadius};
  border: 1px solid ${colors.gray200};
  padding: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: ${spacing.md};
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
  padding: 0 ${spacing.xxxl};
  background: ${colors.gray100};
  border-radius: ${sizes.borderRadius} ${sizes.borderRadius} 0 0;

  @media (max-width: 768px) {
    padding: 0 ${spacing.md};
    border-radius: ${spacing.md} ${spacing.md} 0 0;
  }
`;

const CategoryTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 768px) {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }
`;

const CategoryActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xl};
`;

const OrderButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};

  @media (max-width: 768px) {
    display: none;
  }
`;

const ArrowButton = styled(Button)`
  
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    &.edit-button {
      display: none;
    }
  }
`;

const ProductsList = styled.div<{ hasScroll?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0 ${spacing.lg} ${spacing.lg} ${spacing.lg};
  ${props => props.hasScroll && `
    max-height: calc(4.5 * 80px + ${spacing.lg});
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: ${colors.gray50};
    }

    &::-webkit-scrollbar-thumb {
      background: ${colors.gray300};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${colors.gray400};
    }
  `}

  @media (max-width: 768px) {
    padding: 0 ${spacing.md} ${spacing.md} ${spacing.md};
  }
`;

const ProductRow = styled.div`
  background: ${colors.white};
  border-bottom: 1px solid ${colors.borderLight};
  padding: 0 ${spacing.lg};
  height: 80px;
  min-height: 80px;
  max-height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: ${transitions.normal};
  cursor: pointer;

  &:first-child {
    border-radius: ${sizes.borderRadius} ${sizes.borderRadius} 0 0;
  }

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 ${sizes.borderRadius} ${sizes.borderRadius};
  }

  &:first-child:last-child {
    border-radius: ${sizes.borderRadius};
  }

  &:hover {
    background: ${colors.gray200};
  }

  @media (max-width: 768px) {
    padding: 0 ${spacing.md};

    &:first-child {
      border-radius: ${spacing.md} ${spacing.md} 0 0;
    }

    &:last-child {
      border-radius: 0 0 ${spacing.md} ${spacing.md};
    }

    &:first-child:last-child {
      border-radius: ${spacing.md};
    }
  }
`;

const ProductInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex: 1;
`;

const ProductImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: ${sizes.borderRadiusSmall};
  object-fit: cover;
  background: ${colors.gray100};

  @media (max-width: 768px) {
    display: none;
  }
`;

const ProductImagePlaceholder = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${sizes.borderRadiusSmall};
  background: ${colors.gray100};

  @media (max-width: 768px) {
    display: none;
  }
`;

const ProductName = styled.span`
  font-size: 14px;
  color: ${colors.textPrimary};
  font-weight: 500;

  @media (max-width: 768px) {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }
`;

const HiddenBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: var(--color-error-light);
  border: 1px solid var(--color-error);
  border-radius: 4px;
  font-size: 11px;
  color: var(--color-error);
  font-weight: 500;
  margin-left: 8px;
  white-space: nowrap;
`;

const ProductActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xl};
  justify-content: flex-end;
`;

const ProductOrderButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};

  @media (max-width: 768px) {
    display: none;
  }
`;

const AvailabilitySwitch = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const AvailabilityLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${colors.textSecondary};
  min-width: 65px;

  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const AddButtonText = styled.span`
  .mobile-text {
    display: none;
  }

  @media (max-width: 768px) {
    .desktop-text {
      display: none;
    }
    .mobile-text {
      display: inline;
    }
  }
`;

interface ProductCategorySectionProps {
  category: any;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  addProductBtnRef?: React.Ref<HTMLButtonElement>;
  scrollToProductId?: number | null;
  onScrollComplete?: () => void;
}

export const ProductCategorySection: React.FC<ProductCategorySectionProps> = ({
  category,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  addProductBtnRef,
  scrollToProductId,
  onScrollComplete,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentBranch } = useBranchContext();
  const { isTourOpen, tourStep, stepIndexes, advanceTourStep } = useTourContext();
  const { isTourOpen: attachTourOpen, activeTour: attachActiveTour, tourStep: attachTourStep, advanceStep: advanceAttachStep, editFirstProductRef } = useAdditionsTour();
  const isEditProductStep = attachTourOpen && attachActiveTour === "attach-additions";
  const isFirstCategory = !canMoveUp;
  const [products, setProducts] = useState<any[]>([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [categoryForm] = Form.useForm();
  const productsListRef = useRef<HTMLDivElement>(null);
  const productRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  useQuery<any, Error>(
    [`/products-category-${category.id}`, currentBranch?.id],
    () => getProducts({ categoryId: category.id, branchId: currentBranch?.id }),
    {
      onSuccess(data) {
        setProducts(data);
      },
      enabled: !!currentBranch?.id,
    }
  );

  const { mutateAsync: changeProductOrderMutation } = useMutation(
    changeProductOrder,
    {
      onSuccess: (data) => {
        setProducts(data);
        queryClient.invalidateQueries([`/products-category-${category.id}`, currentBranch?.id]);
      },
    }
  );

  const { mutateAsync: updateProductMutation } = useMutation(
    updateProductAPI,
    {
      onMutate: async (variables) => {
        // Aktualizuj lokalny stan natychmiast PRZED wywołaniem API
        if (variables.productBasicData?.temporaryHidden !== undefined) {
          setProducts((current) =>
            current.map((prod) =>
              prod.id === variables.id
                ? { ...prod, temporaryHidden: variables.productBasicData.temporaryHidden }
                : prod
            )
          );
        }
      },
      onSuccess: () => {
        queryClient.invalidateQueries([`/products-category-${category.id}`, currentBranch?.id]);
        // Also invalidate EditProduct query to update the form
        queryClient.invalidateQueries(["/product-admin"]);
      },
    }
  );

  const { mutateAsync: updateCategoryMutation } = useMutation(
    updateCategory,
    {
      onMutate: async (variables) => {
        // Cancel ongoing queries
        await queryClient.cancelQueries(["/product-categories"]);

        // Snapshot the previous value
        const previousData = queryClient.getQueryData(["/product-categories"]);

        // Optimistically update to the new value
        queryClient.setQueryData(["/product-categories"], (old: any) => {
          if (!Array.isArray(old)) return old;
          return old.map((cat: any) =>
            cat.id === variables.id
              ? { ...cat, ...variables.data }
              : cat
          );
        });

        return { previousData };
      },
      onSuccess: () => {
        // NIE invalidujemy query, aby zachować kolejność
        notification.success({
          message: t("productCategorySection.categoryUpdatedSuccess"),
          placement: "bottomRight",
        });
        setIsCategoryModalOpen(false);
      },
      onError: (_error: any, _variables, context: any) => {
        // Rollback on error
        if (context?.previousData) {
          queryClient.setQueryData(["/product-categories"], context.previousData);
        }
        notification.error({
          message: t("productCategorySection.categoryUpdateError"),
          placement: "bottomRight",
        });
      },
    }
  );

  const { mutateAsync: deleteCategoryMutation } = useMutation(
    removeCategory,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["/product-categories"]);
        notification.success({
          message: t("productCategorySection.categoryDeletedSuccess"),
          placement: "bottomRight",
        });
        setIsCategoryModalOpen(false);
      },
      onError: () => {
        notification.error({
          message: t("productCategorySection.categoryDeleteError"),
          description: t("productCategorySection.categoryDeleteErrorDescription"),
          placement: "bottomRight",
        });
      },
    }
  );

  const handleMoveProductUp = async (productId: number) => {
    const currentIndex = products.findIndex((prod) => prod.id === productId);
    if (currentIndex <= 0) return;

    const newProducts = [...products];
    [newProducts[currentIndex - 1], newProducts[currentIndex]] = [
      newProducts[currentIndex],
      newProducts[currentIndex - 1],
    ];

    const orderedIds = newProducts.map((prod) => prod.id);
    await changeProductOrderMutation({
      categoryId: category.id,
      order: orderedIds,
    });
  };

  const handleMoveProductDown = async (productId: number) => {
    const currentIndex = products.findIndex((prod) => prod.id === productId);
    if (currentIndex < 0 || currentIndex >= products.length - 1) return;

    const newProducts = [...products];
    [newProducts[currentIndex], newProducts[currentIndex + 1]] = [
      newProducts[currentIndex + 1],
      newProducts[currentIndex],
    ];

    const orderedIds = newProducts.map((prod) => prod.id);
    await changeProductOrderMutation({
      categoryId: category.id,
      order: orderedIds,
    });
  };

  const handleToggleAvailability = async (product: any, checked: boolean) => {
    if (!currentBranch) return;

    await updateProductMutation({
      id: product.id,
      branchId: currentBranch.id,
      productBasicData: {
        temporaryHidden: !checked, // odwrotna logika - checked=true znaczy dostępne
      },
    });

    // Show notification with branch name
    notification.success({
      message: t("productCategorySection.productAvailabilityChanged"),
      description: (
        <span>
          {t("productCategorySection.changeAppliedInBranch")} <strong>{currentBranch.name}</strong>
        </span>
      ),
      placement: "bottomRight",
      duration: 3,
    });
  };

  const handleAddProduct = () => {
    setIsAddProductModalOpen(true);
    // Krok 5 tour: kliknięcie "Dodaj produkt" → przejdź do kroku 6
    if (isTourOpen && tourStep === stepIndexes.ADD_PRODUCT_BTN) advanceTourStep(stepIndexes.ADD_PRODUCT_BTN);
  };

  const handleOpenCategoryModal = () => {
    categoryForm.setFieldsValue({ name: category.name });
    setIsCategoryModalOpen(true);
  };

  const handleUpdateCategory = async () => {
    const values = await categoryForm.validateFields();
    await updateCategoryMutation({
      productCategoryId: category.id,
      data: {
        name: values.name,
      },
    });
  };

  const handleDeleteCategory = () => {
    Modal.confirm({
      title: t("productCategorySection.deleteCategoryConfirmTitle"),
      content: t("productCategorySection.deleteCategoryConfirmContent"),
      okText: t("productCategorySection.delete"),
      okType: "danger",
      cancelText: t("productCategorySection.cancel"),
      onOk: async () => {
        await deleteCategoryMutation({ productCategoryId: category.id });
      },
    });
  };

  const handleProductClick = (productId: number) => {
    navigate(`/edit-product/${productId}`);
  };


  // Determine if we should enable scroll (more than 1 category exists)
  const hasScroll = products.length > 5; // Enable scroll if more than 5 products

  useEffect(() => {
    if (!scrollToProductId || products.length === 0) return;
    const productEl = productRefs.current[scrollToProductId];
    if (!productEl) return;
    setTimeout(() => {
      productEl.scrollIntoView({ behavior: "smooth", block: "center" });
      onScrollComplete?.();
    }, 300);
  }, [scrollToProductId, products]);

  const scrollProductsListToEnd = () => {
    // Poczekaj aż lista produktów się zaktualizuje, potem scrolluj na koniec
    setTimeout(() => {
      if (productsListRef.current) {
        productsListRef.current.scrollTop = productsListRef.current.scrollHeight;
      }
    }, 300);
  };

  return (
    <>
      <CategoryContainer>
        <CategoryHeader>
          <CategoryTitleRow>
            <CategoryTitle onClick={handleOpenCategoryModal}>
              {category.name}
            </CategoryTitle>
            <Tooltip title={t("productCategorySection.editCategoryTooltip")}>
              <ArrowButton
                type="text"
                icon={<EditOutlined />}
                onClick={handleOpenCategoryModal}
                className="edit-button"
              />
            </Tooltip>
          </CategoryTitleRow>
          <CategoryActions>
            <Button
              ref={addProductBtnRef}
              type="default"
              size="middle"
              icon={<PlusOutlined />}
              onClick={handleAddProduct}
            >
              <AddButtonText>
                <span className="desktop-text">{t("productCategorySection.addProduct")}</span>
                <span className="mobile-text">{t("productCategorySection.product")}</span>
              </AddButtonText>
            </Button>
            <OrderButtonsGroup>
              <Tooltip title={t("productCategorySection.moveCategoryUp")}>
                <ArrowButton
                  type="text"
                  icon={<CaretUpOutlined />}
                  onClick={onMoveUp}
                  disabled={!canMoveUp}
                />
              </Tooltip>
              <Tooltip title={t("productCategorySection.moveCategoryDown")}>
                <ArrowButton
                  type="text"
                  icon={<CaretDownOutlined />}
                  onClick={onMoveDown}
                  disabled={!canMoveDown}
                />
              </Tooltip>
            </OrderButtonsGroup>
          </CategoryActions>
        </CategoryHeader>

        <ProductsList ref={productsListRef} hasScroll={hasScroll}>
          {products.map((product, index) => {
            const isHiddenInMenu = !product.isVisible;
            return (
              <ProductRow
                key={product.id}
                ref={(el: HTMLDivElement | null) => {
                  productRefs.current[product.id] = el;
                  if (isFirstCategory && index === 0 && isEditProductStep) {
                    (editFirstProductRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                  }
                }}
                onClick={() => {
                  handleProductClick(product.id);
                  // Advance attach tour when user clicks first product in first category
                  if (isFirstCategory && index === 0 && isEditProductStep && (attachTourStep === 3 || attachTourStep === 4)) {
                    setTimeout(() => advanceAttachStep(attachTourStep), 400);
                  }
                }}>
                <ProductInfo>
                  {product.imageUrlS3 ? (
                    <ProductImage
                      src={`${product.imageUrlS3}?t=${new Date().getTime()}`}
                      alt={product.name}
                    />
                  ) : (
                    <ProductImagePlaceholder />
                  )}
                  <ProductName>{product.name}</ProductName>
                </ProductInfo>

                <ProductActions onClick={(e) => e.stopPropagation()}>
                  <AvailabilitySwitch>
                    <AvailabilityLabel>
                      {isHiddenInMenu && (
                        <Tooltip title={t("productCategorySection.hiddenInMenuInBranch", { branch: currentBranch?.name })}>
                          <HiddenBadge>{t("productCategorySection.hiddenInMenu")}</HiddenBadge>
                        </Tooltip>
                      )}
                      {t("productCategorySection.available")}
                    </AvailabilityLabel>
                    <Tooltip
                      title={isHiddenInMenu ? t("productCategorySection.cannotManageAvailabilityDisabled") : ""}
                    >
                      <span>
                        <Switch
                          checked={!product.temporaryHidden && !isHiddenInMenu}
                          disabled={isHiddenInMenu}
                          onChange={(checked) =>
                            handleToggleAvailability(product, checked)
                          }
                        />
                      </span>
                    </Tooltip>
                  </AvailabilitySwitch>

                  <ProductOrderButtonsGroup>
                    <Tooltip title={t("productCategorySection.moveUp")}>
                      <ArrowButton
                        type="text"
                        icon={<CaretUpOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveProductUp(product.id);
                        }}
                        disabled={index === 0}
                      />
                    </Tooltip>

                    <Tooltip title={t("productCategorySection.moveDown")}>
                      <ArrowButton
                        type="text"
                        icon={<CaretDownOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveProductDown(product.id);
                        }}
                        disabled={index === products.length - 1}
                      />
                    </Tooltip>
                  </ProductOrderButtonsGroup>
                </ProductActions>
              </ProductRow>
            );
          })}

          {products.length === 0 && (
            <ProductRow onClick={handleAddProduct}>
              <ProductInfo>
                <ProductName style={{ color: colors.textSecondary }}>
                  {t("productCategorySection.noProductsInCategory")}
                </ProductName>
              </ProductInfo>
            </ProductRow>
          )}
        </ProductsList>
      </CategoryContainer>

      <Modal
        title={t("productCategorySection.editCategoryModalTitle")}
        open={isCategoryModalOpen}
        onOk={handleUpdateCategory}
        onCancel={() => setIsCategoryModalOpen(false)}
        okText={t("productCategorySection.save")}
        cancelText={t("productCategorySection.cancel")}
        footer={[
          <Button key="delete" danger onClick={handleDeleteCategory}>
            {t("productCategorySection.deleteCategory")}
          </Button>,
          <Button key="cancel" onClick={() => setIsCategoryModalOpen(false)}>
            {t("productCategorySection.cancel")}
          </Button>,
          <Button key="save" type="primary" onClick={handleUpdateCategory}>
            {t("productCategorySection.save")}
          </Button>,
        ]}
      >
        <Form form={categoryForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("productCategorySection.categoryNameLabel")}
            rules={[{ required: true, message: t("productCategorySection.categoryNameRequired") }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <AddProductModal
        open={isAddProductModalOpen}
        onCancel={() => setIsAddProductModalOpen(false)}
        categoryId={category.id}
        onProductCreated={scrollProductsListToEnd}
      />
    </>
  );
};
